import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useToast } from './Toast';
import { useEntityList, updateEntity, deleteEntity } from '../data/entities';
import { useCollection } from '../data/firestore';
import { acceptSuggestedPoint, actorOf } from '../data/deals';
import { grantRequirements, grantDraft, grantReview, isAiConfigured } from '../data/legalApi';

// The Application tab of a grant project (projectType 'grant'): the
// opportunity card + stage, the requirement matrix (register rows with
// kind/limit, accepted vs suggested, coverage verdict from the last review),
// the narrative + its run, and the checks / mock-panel panel. Every button
// is one of the /grant/* services; the data is the project's own
// subcollections, so it stays live without any extra plumbing.

export const GRANT_STAGES = [
  ['prospect', 'Prospect'], ['eligible', 'Eligible'], ['drafting', 'Drafting'], ['internal-review', 'Internal review'],
  ['submitted', 'Submitted'], ['awarded', 'Awarded'], ['declined', 'Declined'],
];
const KIND_LABEL = {
  eligibility: 'Eligibility', section: 'Section', form: 'Form', budget: 'Budget', 'page-limit': 'Page limit',
  format: 'Format', attachment: 'Attachment', registration: 'Registration', deadline: 'Deadline', other: 'Other',
};
const VERDICT_TONE = { reflected: 'var(--ok)', partial: '#ffd166', missing: 'var(--accent, #ff6b6b)', 'not-applicable': 'var(--muted)' };
const SEV_TONE = { critical: 'var(--accent, #ff6b6b)', high: '#ffd166', medium: 'var(--info)', low: 'var(--muted)' };

export const fmtMoney = (n) => (Number(n) > 0 ? `$${Math.round(Number(n)).toLocaleString('en-US')}` : '');
export const fmtAmount = (g = {}) => [fmtMoney(g.amountMin), fmtMoney(g.amountMax)].filter(Boolean).join(' – ') || '—';
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
export const daysLeft = (ms) => (ms ? Math.floor((ms - Date.now()) / 86400000) : null);
const limitText = (l) => (l?.pages ? `${l.pages} pp` : l?.words ? `${l.words} words` : l?.chars ? `${l.chars.toLocaleString()} chars` : '');

export function DeadlineChip({ ms }) {
  const d = daysLeft(ms);
  if (d === null) return <span className="cs-chip">no deadline</span>;
  const tone = d < 0 ? 'var(--muted)' : d <= 7 ? 'var(--accent, #ff6b6b)' : d <= 30 ? '#ffd166' : undefined;
  return <span className="cs-chip" style={{ color: tone }}>{fmtDate(ms)}{d >= 0 ? ` · ${d}d` : ' · past'}</span>;
}

export default function GrantApplicationTab({ scope, deal, participant, onGoTo }) {
  const { user } = useAuth();
  const toast = useToast();
  const g = deal.grant || {};
  const { docs: dealPoints } = useEntityList('dealPoint', scope);
  const { docs } = useEntityList('dealDoc', scope);
  const { docs: runs } = useCollection(scope ? `orgs/${scope.org}/deals/${scope.deal}/agentRuns` : null, { orderBy: 'createdAt', dir: 'desc' });
  const [busy, setBusy] = useState('');
  const [filter, setFilter] = useState('all');
  const actor = actorOf(user, participant);

  const draftRun = useMemo(() => (runs || []).find((r) => r.kind === 'grantDraft') || null, [runs]);
  // The LLM passes are queued runs (see api/grantDesk.js): the buttons show
  // "running" from the run doc and the results arrive through the register /
  // deal snapshots, so a slow model never leaves the tab stuck on a spinner.
  const latestRun = (kind) => (runs || []).find((r) => r.kind === kind) || null;
  const reqRun = latestRun('grantRequirements');
  const reviewRun = latestRun('grantReview');
  const running = (r) => r?.status === 'running' && Date.now() - (r.heartbeatAt || r.createdAt || 0) < 5 * 60e3;
  const solicitation = (docs || []).find((d) => d.docType === 'solicitation' || (d.tags || []).includes('solicitation'));
  const narrative = (docs || []).find((d) => d.id === g.narrativeDocId) || (docs || []).find((d) => d.docType === 'grant-narrative') || (draftRun?.result?.docId ? (docs || []).find((d) => d.id === draftRun.result.docId) : null);
  const memos = (docs || []).filter((d) => d.docType === 'panel-review').sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const coverageById = useMemo(() => Object.fromEntries((deal.grantReview?.coverage || []).map((c) => [c.pointId, c])), [deal.grantReview]);
  const checksById = useMemo(() => {
    const m = {};
    for (const f of deal.grantChecks?.findings || []) if (f.pointId) (m[f.pointId] ||= []).push(f);
    return m;
  }, [deal.grantChecks]);

  const rows = useMemo(() => {
    const all = [...(dealPoints || [])].sort((a, b) => (a.suggested === b.suggested ? (a.order || 0) - (b.order || 0) : a.suggested ? 1 : -1));
    if (filter === 'all') return all;
    if (filter === 'suggested') return all.filter((p) => p.suggested);
    if (filter === 'gaps') return all.filter((p) => !p.suggested && (checksById[p.id]?.length || ['missing', 'partial'].includes(coverageById[p.id]?.verdict)));
    return all.filter((p) => (p.kind || 'other') === filter);
  }, [dealPoints, filter, checksById, coverageById]);
  const suggestedCount = (dealPoints || []).filter((p) => p.suggested).length;
  const acceptedCount = (dealPoints || []).length - suggestedCount;
  const sectionCount = (dealPoints || []).filter((p) => !p.suggested && p.kind === 'section').length;

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const r = await fn();
      toast(typeof okMsg === 'function' ? okMsg(r) : okMsg, 'ok');
      return r;
    } catch (err) { toast(err.message, 'danger'); return null; } finally { setBusy(''); }
  };
  const setStage = (stage) => updateEntity('deal', { org: scope.org }, scope.deal, { grant: { ...g, stage } }).catch((e) => toast(e.message, 'danger'));
  const acceptAll = async () => {
    setBusy('accept');
    try {
      for (const p of (dealPoints || []).filter((x) => x.suggested)) await acceptSuggestedPoint(scope, actor, p); // eslint-disable-line no-await-in-loop
      toast('Matrix accepted', 'ok');
    } catch (err) { toast(err.message, 'danger'); } finally { setBusy(''); }
  };

  const checks = deal.grantChecks;
  const review = deal.grantReview;
  const stageIdx = GRANT_STAGES.findIndex(([k]) => k === (g.stage || 'prospect'));

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-5)' }} data-screen="grant-application">
      {/* Opportunity card */}
      <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 320px' }}>
            <div style={{ fontWeight: 600, fontSize: 'var(--fs-lg, 1.1rem)' }}>{g.program || deal.name}</div>
            <div className="u-small u-muted">
              {[g.funder, g.source, g.vehicle, g.phase].filter(Boolean).join(' · ')}
              {g.companyName || g.company ? ` · for ${g.companyName || g.company}` : ''}
            </div>
            {g.notes && <p className="u-small u-muted" style={{ margin: 'var(--sp-2) 0 0' }}>{g.notes}</p>}
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="cs-chip" title="Award range">{fmtAmount(g)}</span>
            {g.matchRequired && <span className="cs-chip" style={{ color: '#ffd166' }}>match required</span>}
            <DeadlineChip ms={g.deadline} />
            <select className="cs-input" value={g.stage || 'prospect'} onChange={(e) => setStage(e.target.value)} aria-label="Application stage" style={{ minWidth: 150 }}>
              {GRANT_STAGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, marginTop: 'var(--sp-3)' }} aria-label="Stage progress">
          {GRANT_STAGES.filter(([k]) => k !== 'declined').map(([k, l], i) => (
            <div key={k} title={l} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= stageIdx ? (g.stage === 'declined' ? 'var(--muted)' : 'var(--ok)') : 'var(--border, #333)' }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', marginTop: 'var(--sp-3)', alignItems: 'center' }}>
          {g.hubDealId && <Link className="cs-btn cs-btn-sm cs-btn-ghost" to={g.profileDocId ? `/deals/${g.hubDealId}/docs/${g.profileDocId}` : `/deals/${g.hubDealId}`}>Company Profile ↗</Link>}
          {solicitation
            ? <Link className="cs-btn cs-btn-sm cs-btn-ghost" to={`/deals/${scope.deal}/docs/${solicitation.id}`}>Solicitation: {solicitation.title.slice(0, 40)}{solicitation.status !== 'ready' ? ` (${solicitation.status})` : ''}</Link>
            : <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => onGoTo?.('documents')}>Add the solicitation →</button>}
          {(g.links || []).map((l) => <a key={l} className="cs-btn cs-btn-sm cs-btn-ghost" href={l} target="_blank" rel="noreferrer">{l.replace(/^https?:\/\//, '').slice(0, 32)} ↗</a>)}
          <Link className="cs-btn cs-btn-sm cs-btn-ghost" to="/grants" style={{ marginLeft: 'auto' }}>All grants</Link>
        </div>
      </div>

      {/* Pipeline: the three actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-3)' }}>
        <div className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
          <div style={{ fontWeight: 600 }}>1 · Requirements</div>
          <div className="u-xs u-muted" style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>
            {acceptedCount ? `${acceptedCount} accepted · ${sectionCount} sections` : 'Nothing extracted yet'}{suggestedCount ? ` · ${suggestedCount} to review` : ''}
            {reqRun?.status === 'error' && <span style={{ color: 'var(--accent, #ff6b6b)' }}> · last run failed: {reqRun.error}</span>}
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy || running(reqRun) || !isAiConfigured() || !solicitation || solicitation.status !== 'ready'} title={!solicitation ? 'Add the solicitation first' : solicitation.status !== 'ready' ? 'The solicitation is still ingesting' : ''}
              onClick={() => run('req', () => grantRequirements({ orgId: scope.org, dealId: scope.deal }), 'Reading the solicitation — rows appear below as suggestions (1–2 min)')}>
              {busy === 'req' || running(reqRun) ? 'Extracting…' : acceptedCount || suggestedCount ? 'Re-extract' : 'Extract from solicitation'}
            </button>
            {suggestedCount > 0 && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!!busy} onClick={acceptAll}>{busy === 'accept' ? 'Accepting…' : `Accept all ${suggestedCount}`}</button>}
          </div>
        </div>
        <div className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
          <div style={{ fontWeight: 600 }}>2 · Narrative</div>
          <div className="u-xs u-muted" style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>
            {running(draftRun) ? `Drafting… ${draftRun.progress?.label || ''}` : narrative ? `${narrative.title}${draftRun?.result?.confirms ? ` · ${draftRun.result.confirms} [CONFIRM]` : ''}` : draftRun?.status === 'error' ? `Last run failed: ${draftRun.error}` : 'Not drafted yet'}
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy || !isAiConfigured() || running(draftRun)}
              onClick={() => run('draft', () => grantDraft({ orgId: scope.org, dealId: scope.deal }), (r) => `Drafting ${r.sections || 'the standard'} sections${r.usingSuggested ? ' from the suggested rows' : ''} — a few minutes; the narrative lands in Documents`)}>
              {busy === 'draft' ? 'Queuing…' : running(draftRun) ? 'Drafting…' : narrative ? 'Redraft narrative' : 'Draft narrative'}
            </button>
            {narrative && <Link className="cs-btn cs-btn-sm cs-btn-ghost" to={`/deals/${scope.deal}/docs/${narrative.id}`}>Open ↗</Link>}
          </div>
        </div>
        <div className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
          <div style={{ fontWeight: 600 }}>3 · Review</div>
          <div className="u-xs u-muted" style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>
            {running(reviewRun) ? 'Reviewing — checks, coverage and the panel land here when done (1–3 min)' : review ? `Panel ${review.score}/5 · ${review.fundable} · ${fmtDate(review.at)}` : 'No review yet'}{checks?.findings?.length ? ` · ${checks.findings.length} check${checks.findings.length === 1 ? '' : 's'}` : ''}
            {reviewRun?.status === 'error' && <span style={{ color: 'var(--accent, #ff6b6b)' }}> · last run failed: {reviewRun.error}</span>}
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy || running(reviewRun) || !isAiConfigured() || !narrative || running(draftRun)}
              onClick={() => run('review', () => grantReview({ orgId: scope.org, dealId: scope.deal }), 'Review queued — scores and the memo appear here when it finishes')}>
              {busy === 'review' || running(reviewRun) ? 'Reviewing…' : review ? 'Review again' : 'Check & mock panel'}
            </button>
            {memos[0] && <Link className="cs-btn cs-btn-sm cs-btn-ghost" to={`/deals/${scope.deal}/docs/${memos[0].id}`}>Latest memo ↗</Link>}
          </div>
        </div>
      </div>

      {/* Checks + panel */}
      {(checks || review) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--sp-3)' }}>
          {checks && (
            <div className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
              <div style={{ fontWeight: 600 }}>Checks <span className="u-xs u-muted">≈{checks.pages} pages · {checks.charsPerPage} chars/page{checks.usingSuggested ? ' · matrix not yet accepted' : ''}</span></div>
              {!checks.findings?.length && <div className="u-small" style={{ color: 'var(--ok)', marginTop: 'var(--sp-2)' }}>No limit, placeholder or deadline findings.</div>}
              <div style={{ display: 'grid', gap: 'var(--sp-1)', marginTop: 'var(--sp-2)' }}>
                {(checks.findings || []).map((f, i) => (
                  <div key={i} className="u-small" style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                    <span className="cs-chip" style={{ color: SEV_TONE[f.severity], flex: '0 0 auto' }}>{f.severity}</span>
                    <span><strong>{f.title}</strong> — <span className="u-muted">{f.note}</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {review && (
            <div className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
              <div style={{ fontWeight: 600 }}>Mock panel <span className="u-xs u-muted">{review.family} criteria · {review.score}/5 · {review.fundable}</span></div>
              <div style={{ display: 'grid', gap: 'var(--sp-1)', marginTop: 'var(--sp-2)' }}>
                {(review.criteria || []).map((c) => (
                  <div key={c.name} className="u-small" style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
                    <div style={{ flex: '0 0 80px', height: 6, borderRadius: 3, background: 'var(--border, #333)' }}><div style={{ width: `${(c.score / 5) * 100}%`, height: '100%', borderRadius: 3, background: c.score >= 4 ? 'var(--ok)' : c.score >= 3 ? '#ffd166' : 'var(--accent, #ff6b6b)' }} /></div>
                    <span>{c.name} <span className="u-muted">{c.score}/5</span></span>
                  </div>
                ))}
              </div>
              {review.topFixes?.length > 0 && (
                <ol className="u-small" style={{ margin: 'var(--sp-2) 0 0', paddingLeft: 'var(--sp-5)' }}>
                  {review.topFixes.map((f, i) => <li key={i}>{f}</li>)}
                </ol>
              )}
            </div>
          )}
        </div>
      )}

      {/* Requirements matrix */}
      <div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--sp-2)' }}>
          <h3 style={{ margin: 0 }}>Requirement matrix</h3>
          {[['all', 'All'], ['suggested', `Suggested (${suggestedCount})`], ['gaps', 'Gaps'], ['section', 'Sections'], ['eligibility', 'Eligibility'], ['form', 'Forms'], ['registration', 'Registrations'], ['budget', 'Budget']].map(([k, l]) => (
            <button key={k} type="button" className={`cs-btn cs-btn-sm ${filter === k ? 'cs-btn-primary' : 'cs-btn-ghost'}`} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
        {!(dealPoints || []).length && <div className="cs-empty">No requirements yet — add the solicitation and extract, or add rows by hand on the Deal points tab.</div>}
        <div style={{ display: 'grid', gap: 'var(--sp-1)' }}>
          {rows.map((p) => {
            const cov = coverageById[p.id];
            const chk = checksById[p.id] || [];
            return (
              <div key={p.id} className="cs-card" style={{ padding: 'var(--sp-2) var(--sp-3)', display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap', opacity: p.suggested ? 0.85 : 1 }}>
                <span className="cs-chip" style={{ minWidth: 84, textAlign: 'center' }}>{KIND_LABEL[p.kind] || KIND_LABEL.other}</span>
                <div style={{ flex: '1 1 300px', minWidth: 220 }}>
                  <div style={{ fontWeight: 600 }}>{p.title}{p.suggested && <span className="u-xs u-muted"> · suggested</span>}</div>
                  {p.detail && <div className="u-xs u-muted" style={{ whiteSpace: 'pre-wrap' }}>{String(p.detail).slice(0, 280)}</div>}
                </div>
                {limitText(p.limit) && <span className="cs-chip" title="Limit">{limitText(p.limit)}</span>}
                {p.priority === 'critical' && <span className="cs-chip" style={{ color: SEV_TONE.critical }}>critical</span>}
                {chk.map((f, i) => <span key={i} className="cs-chip" style={{ color: SEV_TONE[f.severity] }} title={f.note}>{f.kind}</span>)}
                {cov && <span className="cs-chip" style={{ color: VERDICT_TONE[cov.verdict] }} title={cov.evidence}>{cov.verdict}</span>}
                {p.suggested ? (
                  <>
                    <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" onClick={() => acceptSuggestedPoint(scope, actor, p).catch((e) => toast(e.message, 'danger'))}>Accept</button>
                    <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => deleteEntity('dealPoint', scope, p.id).catch((e) => toast(e.message, 'danger'))}>Dismiss</button>
                  </>
                ) : (
                  <span className="cs-chip" style={{ color: p.status === 'agreed' ? 'var(--ok)' : undefined }}>{p.status === 'agreed' ? 'done' : p.status}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
